import { Box, Container, Typography } from '@mui/material';

export const PrivacyPage = () => {
  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="md">
        <Typography
          variant="h1"
          sx={{
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
          }}
        >
          Privacy Policy
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Introduction */}
          <Box>
            <Typography variant="body1">
              This Privacy Policy describes how I, Tarlan Khakimzade, the
              individual developer of the Calorium app ("I", "me", "my"),
              collect, use, store, and process user data when you use the
              Calorium mobile application (the "App"). By installing and using
              the App, you agree to the terms of this Privacy Policy.
            </Typography>
          </Box>

          {/* Information I Collect */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              1. Information I Collect
            </Typography>

            <Typography variant="h3" sx={{ mb: 1.5, fontSize: '1.25rem' }}>
              1.1 Personal Data
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              You may voluntarily provide me with personal information such as
              your name, email address, and other data that can identify you.
              This information may be collected during registration,
              subscription, profile setup, etc.
            </Typography>

            <Typography variant="h3" sx={{ mb: 1.5, fontSize: '1.25rem' }}>
              1.2 Non-Personal Data
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              I also collect anonymous data that does not directly identify you,
              such as task names, food intake dates and times, goals, product
              categories, calories, and macronutrients.
            </Typography>

            <Typography variant="h3" sx={{ mb: 1.5, fontSize: '1.25rem' }}>
              1.3 Third-Party Data
            </Typography>
            <Typography variant="body1">
              I may receive information from analytics and marketing services
              such as Firebase, Amplitude, Apphud, Appsflyer, Facebook SDK, and
              others. This may include device characteristics, in-app activity,
              subscription data, and demographic details.
            </Typography>
          </Box>

          {/* How I Use Your Data */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              2. How I Use Your Data
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Collected data is used for:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Ensuring stable and secure operation of the App
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Improving user experience and developing new features
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Supporting motivation and personalizing content
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Sending push notifications (with your consent)
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Integration with AI systems (e.g., calculating calorie and
                macronutrient values using OpenAI's GPT‑API)
              </Typography>
            </Box>
          </Box>

          {/* Data Sharing */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              3. Data Sharing and Disclosure
            </Typography>
            <Typography variant="h3" sx={{ mb: 1.5, fontSize: '1.25rem' }}>
              3.1 Third Parties and Partners
            </Typography>
            <Typography variant="body1">
              I do not sell your personal data. However, I may share data with
              trusted partners and contractors who help me provide the service —
              for example, for analytics, cloud storage, or payment processing.
              These parties are required to protect your information.
            </Typography>
          </Box>

          {/* Security */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              4. Security
            </Typography>
            <Typography variant="body1">
              I take reasonable measures to protect your data from unauthorized
              access, loss, or destruction. However, data transmission over the
              internet is never entirely secure, and I cannot guarantee absolute
              protection.
            </Typography>
          </Box>

          {/* Children's Privacy */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              5. Children's Privacy
            </Typography>
            <Typography variant="body1">
              The Calorium app is not intended for children under the age of 13.
              I do not knowingly collect information from children. If such
              information is discovered, it will be deleted.
            </Typography>
          </Box>

          {/* Policy Changes */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              6. Policy Changes
            </Typography>
            <Typography variant="body1">
              I may update this Privacy Policy from time to time. All changes
              will be posted on this page, and the update date will be indicated
              at the top. Continued use of the App after updates constitutes
              your agreement to the new terms.
            </Typography>
          </Box>

          {/* User Consent */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              7. User Consent
            </Typography>
            <Typography variant="body1">
              By using Calorium, you agree to the terms of this Privacy Policy.
              If you do not agree, please do not use the App.
            </Typography>
          </Box>

          {/* Contact */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              8. Contact Me
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              If you have questions, suggestions, or complaints, you can contact
              me at:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Email:{' '}
                <Box
                  component="a"
                  href="mailto:x.tarlan@yandex.ru"
                  sx={{ color: 'primary.main', textDecoration: 'none' }}
                >
                  x.tarlan@yandex.ru
                </Box>
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Website:{' '}
                <Box
                  component="a"
                  href="https://calorium.app"
                  sx={{ color: 'primary.main', textDecoration: 'none' }}
                >
                  https://calorium.app
                </Box>
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Address: Azerbaijan, Baku, Moscow Avenue, 48
              </Typography>
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              mt: 4,
              pt: 4,
              borderTop: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © 2025 Calorium by Tarlan Khakimzade
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
